import { getPur, pageParams, PurClientConfig, readBoolean, readDecimal, readId, readObject, readPage, readText, requireId } from "./purRead";

export const PUR_DOCUMENT_TYPES = ["inv", "tax_inv", "rcp", "tax_inv_rcp", "dlv", "crd", "wht"] as const;
export type PurDocumentType = typeof PUR_DOCUMENT_TYPES[number];

export interface PurDocumentLine {
  id: number | null;
  documentId: number | null;
  poItemId: number | null;
  receiptItemId: number | null;
  productId: number | null;
  productName: string;
  invoicedQuantity: string | null;
  unitCost: string | null;
  taxableAmount: string | null;
  vatAmount: string | null;
  lineTotal: string | null;
  priceVariance: string | null;
  notes: string;
}

export interface PurDocument {
  id: number;
  number: string;
  date: string;
  createdAt: string;
  editedAt: string;
  type: string;
  vendorId: number | null;
  vendorName: string;
  projectId: number | null;
  projectNumber: string;
  poId: number | null;
  receiptId: number | null;
  verified: boolean | null;
  grossAmount: string | null;
  taxableAmount: string | null;
  vatAmount: string | null;
  exemptAmount: string | null;
  withholdingAmount: string | null;
  totalPriceVariance: string | null;
  hasPriceVariance: boolean | null;
  attachmentUrl: string | null;
  // null means this deployment did not provide nested lines; [] means empty.
  items: PurDocumentLine[] | null;
}

export interface PurDocumentQuery { limit: number; offset: number; type?: PurDocumentType; verified?: boolean }
export interface PurDocumentPage { count: number; records: PurDocument[] }
export interface PurDocumentLinePage { count: number; records: PurDocumentLine[] }

const safeAttachment = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") && !url.username && !url.password ? url.href : null;
  } catch { return null; }
};

export const normalizePurDocumentLine = (value: unknown): PurDocumentLine => {
  const line = readObject(value);
  return {
    id: readId(line.id), documentId: readId(line.document), poItemId: readId(line.po_item), receiptItemId: readId(line.receipt_item),
    productId: readId(line.product), productName: readText(line.product_name),
    invoicedQuantity: readDecimal(line.invoiced_qty), unitCost: readDecimal(line.unit_cost),
    taxableAmount: readDecimal(line.taxable_amount), vatAmount: readDecimal(line.vat_amount), lineTotal: readDecimal(line.line_total),
    priceVariance: readDecimal(line.price_variance), notes: readText(line.notes),
  };
};

export const normalizePurDocument = (value: unknown): PurDocument => {
  const doc = readObject(value);
  const id = requireId(doc.id);
  return {
    id, number: readText(doc.doc_number) || String(id), date: readText(doc.doc_date), type: readText(doc.doc_type),
    createdAt: readText(doc.created), editedAt: readText(doc.edited),
    vendorId: readId(doc.vendor), vendorName: readText(doc.vendor_name), projectId: readId(doc.project), projectNumber: readText(doc.project_number),
    poId: readId(doc.po), receiptId: readId(doc.receipt), verified: readBoolean(doc.verification_status),
    grossAmount: readDecimal(doc.gross_amount), taxableAmount: readDecimal(doc.taxable_amount), vatAmount: readDecimal(doc.vat_amount),
    exemptAmount: readDecimal(doc.tax_exempt_amount), withholdingAmount: readDecimal(doc.wht_amount),
    totalPriceVariance: readDecimal(doc.total_price_variance), hasPriceVariance: readBoolean(doc.has_price_variance),
    attachmentUrl: safeAttachment(doc.file_attachment),
    items: Array.isArray(doc.items) ? doc.items.map(normalizePurDocumentLine) : null,
  };
};

export const listPurDocuments = async (config: PurClientConfig, query: PurDocumentQuery): Promise<PurDocumentPage> => {
  const params = pageParams(query.limit, query.offset);
  if (query.type) {
    if (!PUR_DOCUMENT_TYPES.includes(query.type)) throw new Error("Invalid PUR document type");
    params.set("doc_type", query.type);
  }
  if (query.verified !== undefined) params.set("verification_status", String(query.verified));
  return readPage(await getPur(config, `documents/?${params.toString()}`), normalizePurDocument);
};

export const getPurDocument = async (config: PurClientConfig, id: number): Promise<PurDocument> =>
  normalizePurDocument(await getPur(config, `documents/${requireId(id)}/`));

export const listPurDocumentLines = async (config: PurClientConfig, id: number, limit: number, offset: number): Promise<PurDocumentLinePage> =>
  readPage(await getPur(config, `documents/${requireId(id)}/items/?${pageParams(limit, offset).toString()}`), normalizePurDocumentLine);
